import { useCallback } from 'react'
import { toast } from 'sonner'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'
import { type Mention, mentionIds, mentionRect } from './mentions'

/** Remove the quote highlight, if any. */
export function clearQuoteHighlight() {
  if (typeof CSS === 'undefined' || !('highlights' in CSS)) return
  ;(CSS as unknown as { highlights: Map<string, unknown> }).highlights.delete('moot-quote')
}

/**
 * Find `quote` in the card's rendered text and mark it with the CSS Highlight
 * API. With `ms`, the mark goes away after that long.
 */
export function highlightQuote(cardId: string, quote: string, ms: number | null = 2600) {
  const root = document.querySelector(`[data-card-id="${CSS.escape(cardId)}"] .ProseMirror`)
  if (!root || typeof CSS === 'undefined' || !('highlights' in CSS)) return
  const nodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text)
  const full = nodes.map((n) => n.data).join('')
  const at = full.indexOf(quote)
  if (at < 0) return
  const range = document.createRange()
  let offset = 0
  for (const n of nodes) {
    const end = offset + n.data.length
    if (at >= offset && at <= end) range.setStart(n, at - offset)
    if (at + quote.length >= offset && at + quote.length <= end) {
      range.setEnd(n, at + quote.length - offset)
      break
    }
    offset = end
  }
  const registry = (CSS as unknown as { highlights: Map<string, unknown> }).highlights
  const HighlightCtor = (window as unknown as { Highlight?: new (r: Range) => unknown }).Highlight
  if (!HighlightCtor) return
  registry.set('moot-quote', new HighlightCtor(range))
  if (ms !== null) setTimeout(() => registry.delete('moot-quote'), ms)
}

/**
 * Show what a mention points at: bring the canvas into view (in chat-main the
 * side canvas slides in), move the camera to it and briefly emphasise it.
 */
export function useFocusMention() {
  const { client, engine, canvas } = useBoardCtx()
  const { t } = useI18n()
  return useCallback(
    async (m: Mention) => {
      const rect = mentionRect(m, client.view)
      if (!rect) {
        toast(t('mention.gone'))
        return
      }
      // The canvas may close, or mount again (StrictMode registers it twice),
      // while it comes in: move only the camera that is there and settled.
      let api = await canvas.reveal()
      for (let tries = 0; engine.current !== api && tries < 3; tries++) {
        if (!engine.current) return
        api = await canvas.reveal()
      }
      if (engine.current !== api) return
      api.fitRect(rect)
      api.flash(mentionIds(m, client.view))
      if (m.kind === 'item' && m.quote) {
        const quote = m.quote
        setTimeout(() => {
          highlightQuote(m.id, quote)
        }, 500)
      }
    },
    [client, engine, canvas, t],
  )
}
